import React, { type ChangeEvent, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import { useOnboarding } from 'components/member_onboarding_v2/OnboardingContext'
import { hasExistingOnThatchEnrollments } from 'components/member_onboarding_v2/utils'
import {
  FormCheckboxFieldDS,
  FormDateFieldDS,
  FormRadioGroupDS,
  FormSelectFieldDS,
  FormTextFieldDS,
} from 'components/reusable/form/FormFields'
import { Banner, Button, ButtonGroup, Tooltip, useBreakpoint } from 'slab'
import { calculateAgeAtDate, formatSSN, getFormattedDate } from 'utils'
import {
  type MemberOnboardingAdditionalParticipantGender,
  type MemberOnboardingAdditionalParticipantRelationship,
} from 'utils/web_api.schemas'

import { OnboardingGenericErrorMessage } from '../OnboardingGenericErrorMessage'

interface RenewalsAddDependentProps {
  genderOptions: { value: string; label: string }[];
  handleCancel: () => void;
  handlePostAction: () => void;
  hasExistingSpouse: boolean;
  onTriggerModal: () => void;
  modalClosed: boolean;
  modalSubmitted: boolean;
}

export interface ParticipantFormProps {
  given_name: string;
  family_name: string;
  dob: string;
  ssn: string;
  gender: MemberOnboardingAdditionalParticipantGender;
  relationship: MemberOnboardingAdditionalParticipantRelationship;
  has_disability?: boolean | null;
  skip_ssn_for_newborn?: boolean;
  // tobacco is required from the model so marking it as required here as well
  // from the UI we are sending "false" by default (since false is a default value)
  // but we can consider making this optional from the UI & API and have the backend handle this.
  tobacco: boolean;
}

// This component will only be used for additional participants.
const RenewalsAddDependent: React.FC<RenewalsAddDependentProps> = ({
  genderOptions,
  handleCancel,
  handlePostAction,
  hasExistingSpouse,
  onTriggerModal,
  modalClosed,
  modalSubmitted,
}) => {
  const {
    onboardingData,
    createAdditionalParticipant,
    refreshAdditionalParticipants,
    refreshOnboardingInfo,
  } = useOnboarding()
  const { breakpointIsAtLeast } = useBreakpoint()

  const [submitError, setSubmitError] = useState<React.ReactElement | null>(
    null
  )
  const [pendingFormData, setPendingFormData] =
    useState<ParticipantFormProps | null>(null)
  const [showDisabilityCheckbox, setShowDisabilityCheckbox] =
    useState<boolean>(false)
  const [showSkipSSNCheckbox, setShowSkipSSNCheckbox] =
    useState<boolean>(false)

  // === useEffects ===
  useEffect(() => {
    if (modalSubmitted && pendingFormData) {
      onSubmit(pendingFormData)
    }
  }, [modalSubmitted, pendingFormData])

  useEffect(() => {
    if (modalClosed && pendingFormData) {
      setPendingFormData(null)
    }
  }, [modalClosed, pendingFormData])

  // === Get defaults for card ===
  const getDefaultRelationship =
    (): MemberOnboardingAdditionalParticipantRelationship => {
      if (hasExistingSpouse) {
        return 'child'
      }
      return 'spouse'
    }

  const defaultValues = {
    given_name: '',
    family_name: '',
    dob: '',
    ssn: '',
    gender: '' as MemberOnboardingAdditionalParticipantGender,
    relationship: getDefaultRelationship(),
    tobacco: false,
    has_disability: null,
    skip_ssn_for_newborn: false,
  }

  const massageErrorMessages = (errorMessages: string[]) => {
    return errorMessages.map((message) => {
      if (message.includes('25 years')) {
        return 'children over 25 years old must have a disability to be on your plan'
      }
      return message
    })
  }

  const {
    control,
    register,
    handleSubmit,
    reset,
    setError,
    watch,
    clearErrors,
    setValue,
    formState: { errors, dirtyFields, isValid },
  } = useForm<ParticipantFormProps>({
    defaultValues,
    mode: 'onChange',
  })

  // This hook is needed in case the data refreshes.
  useEffect(() => {
    reset(defaultValues)
  }, [getDefaultRelationship()])

  const onHandleError = (error: GrapeAPIErrorType) => {
    if (error.status === 422) {
      const fieldErrors = error.fieldErrors!
      Object.entries(fieldErrors).forEach(([field, errorMessages]) => {
        const massagedErrors = massageErrorMessages(errorMessages)
        setError(field as keyof ParticipantFormProps, {
          type: 'manual',
          message: massagedErrors.join(', '),
        })
      })
    } else {
      const errorMessage = <OnboardingGenericErrorMessage error={error} />
      setSubmitError(errorMessage)
    }
  }

  const enrollmentsPresent = hasExistingOnThatchEnrollments(onboardingData)

  const mutation = createAdditionalParticipant

  const relationship = watch('relationship')
  const dob = watch('dob')
  const givenName = watch('given_name')
  const hasDisability = watch('has_disability')
  const skipSSNForNewborn = watch('skip_ssn_for_newborn')

  // === Disability checkbox logic ===
  const decideIfShowDisabilityCheckbox = () => {
    if (shouldShowDisabilityCheckbox()) {
      setShowDisabilityCheckbox(true)
    } else {
      setShowDisabilityCheckbox(false)
    }
  }

  useEffect(() => {
    if (dob) {
      // We only show disability check if the year has four digits
      // So that we can prevent showing the disability check while people are entering the date
      if (new Date(dob).getFullYear() >= 1000) {
        decideIfShowDisabilityCheckbox()

        // Check if participant is a newborn (less than 1 year old)
        const age = calculateAgeAtDate(dob, new Date())
        const isNewborn = age < 1
        setShowSkipSSNCheckbox(isNewborn)
      } else {
        // We hide the disability check, the year will still be validated
        // based on the validation rule from DOB field below
        setShowDisabilityCheckbox(false)
        setShowSkipSSNCheckbox(false)
      }
    } else {
      // If there is no DOB, we hide both checkboxes
      setShowDisabilityCheckbox(false)
      setShowSkipSSNCheckbox(false)
    }
  }, [relationship, dob, decideIfShowDisabilityCheckbox])

  // Clear SSN field when skip checkbox is checked
  useEffect(() => {
    if (skipSSNForNewborn) {
      setValue('ssn', '')
      clearErrors('ssn')
    }
  }, [skipSSNForNewborn, setValue, clearErrors])

  const soonestCoverageStartDate =
    onboardingData?.member?.soonest_coverage_start_date

  // Helper function to check if a child is over 25 at coverage start date
  const isChildOver25AtCoverageStart = (
    birthDate: string | null | undefined
  ): boolean => {
    if (!birthDate) return false
    if (!soonestCoverageStartDate) return false

    const age = calculateAgeAtDate(birthDate, soonestCoverageStartDate)
    return age > 25
  }

  const shouldShowDisabilityCheckbox = () => {
    if (relationship === 'child' && dob && soonestCoverageStartDate) {
      return isChildOver25AtCoverageStart(dob)
    }
    return false
  }

  const shouldDisableSubmitButton = () => {
    if (shouldShowDisabilityCheckbox() && !hasDisability) {
      return true
    }
    return false
  }

  useEffect(() => {
    if (shouldDisableSubmitButton()) {
      setError('has_disability', {
        type: 'manual',
        message: 'You must check this box or remove this dependent to proceed',
      })
    } else {
      clearErrors('has_disability')
    }
  }, [shouldDisableSubmitButton()])

  // === Submit form logic ===
  const maybeSubmitForm = (formData: ParticipantFormProps) => {
    if (dirtyFields.dob && enrollmentsPresent) {
      setPendingFormData(formData)
      onTriggerModal()
    } else {
      onSubmit(formData)
    }
  }

  const onSubmit = (formData: ParticipantFormProps) => {
    // reset has_disability if the relationship is spouse
    if (formData.relationship === 'spouse' && formData.has_disability) {
      formData.has_disability = null
    }

    // reset has_disability if the child is not over 25 at the coverage start date
    if (formData.relationship === 'child' && formData.has_disability) {
      if (!isChildOver25AtCoverageStart(formData.dob)) {
        formData.has_disability = null
      }
    }

    // Validate that has_disability is checked for children over 25
    if (
      formData.relationship === 'child' &&
      isChildOver25AtCoverageStart(formData.dob) &&
      !formData.has_disability
    ) {
      setError('has_disability', {
        type: 'manual',
        message: 'You must check this box or remove this dependent to proceed',
      })
      return
    }

    // Clear SSN if skip_ssn_for_newborn is checked
    if (formData.skip_ssn_for_newborn) {
      formData.ssn = ''
    }

    // Remove skip_ssn_for_newborn from form data before submitting (it's not a backend field)
    const dataToSubmit = { ...formData }
    delete dataToSubmit.skip_ssn_for_newborn

    mutation.mutate(dataToSubmit, {
      onSuccess: () => {
        window.segment?.track('[EE] Dependent added', {
          enrollment_event_id: onboardingData?.enrollment_event.id,
          enrollment_event_type: onboardingData?.enrollment_event.type,
          enrollment_event_subtype: onboardingData?.enrollment_event.subtype,
          relationship: dataToSubmit.relationship,
        })
        refreshAdditionalParticipants()
        refreshOnboardingInfo()
        handlePostAction()
      },
      onError: (error) => {
        onHandleError(error)
      },
    })
  }

  // === Render ===

  // Buttons
  function renderButtons () {
    const cancelButton = (
      <Button
        onClick={() => {
          handleCancel()
        }}
        variant='tertiary-deemphasized'
        disabled={mutation.isPending}
      >
        Cancel
      </Button>
    )

    const submitButton = (
      <Button
        type='submit'
        variant='secondary'
        disabled={mutation.isPending || shouldDisableSubmitButton() || !isValid}
      >
        Save changes
      </Button>
    )

    if (breakpointIsAtLeast('xs')) {
      return (
        <ButtonGroup size='large'>
          {cancelButton}
          {submitButton}
        </ButtonGroup>
      )
    }

    return (
      <ButtonGroup size='large' layout='vertical'>
        {submitButton}
        {cancelButton}
      </ButtonGroup>
    )
  }

  return (
    <form
      className='border border-gray-200 rounded-lg p-3 shadow-sm bg-thatch-50 gap-y-3 flex flex-col'
      onSubmit={handleSubmit(maybeSubmitForm)}
      onBlur={(e) => { // Auto-save when user leaves the form
        if (!isValid) return // Only save when form is valid
        if (Object.keys(dirtyFields).length === 0) return // Ignore if nothing changed
        if (e.currentTarget.contains(e.relatedTarget as Node)) return // Ignore blurs between inputs
        handleSubmit(maybeSubmitForm)()
      }}
    >
      <div className='p-4 space-y-4'>
        <div className='grid grid-cols-2 gap-x-4 gap-y-3'>
          {/* First name */}
          <FormTextFieldDS<ParticipantFormProps>
            error={errors.given_name}
            fieldId='given_name'
            fieldLabel='First name'
            register={register}
            placeholder='E.g. Jane'
            disabled={mutation.isPending}
            validationRule={{ required: 'First name is required.' }}
          />
          {/* Last name */}
          <FormTextFieldDS<ParticipantFormProps>
            error={errors.family_name}
            fieldId='family_name'
            fieldLabel='Last name'
            register={register}
            placeholder='E.g. Doe'
            disabled={mutation.isPending}
            validationRule={{ required: 'Last name is required.' }}
          />
          {/* Date of birth */}
          <FormDateFieldDS<ParticipantFormProps>
            error={errors.dob}
            fieldId='dob'
            fieldLabel='Date of birth'
            register={register}
            placeholder='YYYY-MM-DD'
            disabled={mutation.isPending}
            validationRule={{
              required: 'Date of birth is required.',
              validate: {
                validYear: (value: string) => {
                  const age = calculateAgeAtDate(value, new Date())
                  if (age > 120) {
                    return 'Date of birth cannot be more than 120 years ago'
                  } else if (age < 0) {
                    return 'Date of birth cannot be in the future'
                  }
                  return true
                },
              },
            }}
          />
          {/* SSN */}
          <FormTextFieldDS<ParticipantFormProps>
            fieldId='ssn'
            fieldLabel='SSN or ITIN'
            register={register}
            placeholder='XXX-XX-XXXX'
            error={errors.ssn}
            disabled={mutation.isPending || skipSSNForNewborn}
            validationRule={{
              required: skipSSNForNewborn ? false : 'SSN or ITIN is required',
              pattern: {
                value: /^\d{3}-?\d{2}-?\d{4}$/,
                message: 'Please enter a valid SSN or ITIN (XXX-XX-XXXX)',
              },
              onChange: (e: ChangeEvent<HTMLInputElement>) => {
                e.target.value = formatSSN(e.target.value)
              },
            }}
          />
        </div>
        <div>
          {/* Gender */}
          <FormSelectFieldDS<ParticipantFormProps>
            fieldId='gender'
            fieldLabel='Gender'
            fieldOptions={genderOptions}
            description={
              genderOptions.length === 3
                ? "We'll use the gender you specify wherever possible, but are limited to the options that insurance companies can usually accept."
                : "While we recognize that gender is a spectrum, some insurance companies' systems require that we use binary sex."
            }
            register={register}
            placeholder='Select an option'
            error={errors.gender}
            disabled={mutation.isPending}
            validationRule={{
              required: 'Gender is required',
            }}
          />
        </div>
        {/* Spouse/Child radios -- only show tooltip for Spouse radio when disabled */}
        <div>
          <Tooltip
            content="You've already added a spouse"
            enabled={hasExistingSpouse}
            side='top'
            align='start'
            arrowAlign='start'
          >
            <div>
              <FormRadioGroupDS
                control={control}
                name='relationship'
                label='Relationship'
                options={[
                  {
                    label: 'Spouse',
                    value: 'spouse',
                    disabled: hasExistingSpouse,
                  },
                  { label: 'Child', value: 'child' },
                ]}
                disabled={mutation.isPending}
              />
            </div>
          </Tooltip>
        </div>
        {/* Skip SSN for newborn checkbox */}
        {showSkipSSNCheckbox && (
          <FormCheckboxFieldDS
            fieldId='skip_ssn_for_newborn'
            fieldLabel='Skip SSN entry (newborn under 1 year old)'
            register={register}
            disabled={mutation.isPending}
            error={errors.skip_ssn_for_newborn}
          />
        )}
        {/* Disability checkbox */}
        {showDisabilityCheckbox && soonestCoverageStartDate && (
          <Banner
            title={(
              <>
                <span className='sensitive'>{givenName || 'Your child'}</span>{' '}
                will be over 25 by {getFormattedDate(soonestCoverageStartDate, {
                  month: 'long',
                  timeZone: 'UTC',
                })}
              </>
            )}
            type='warning'
          >
            <p>
              Due to their age, <span className='sensitive'>{givenName || 'your child'}</span> can only be added as
              a dependent on your plan if they have a disability.
            </p>
            <FormCheckboxFieldDS
              fieldId='has_disability'
              fieldLabel='Exempt from age limit due to disability'
              register={register}
              disabled={mutation.isPending}
              error={errors.has_disability}
            />
          </Banner>
        )}
      </div>
      {/* Error on submit */}
      {submitError && (
        <div className='mb-10'>
          <Banner
            title='A problem has occurred while processing your request'
            type='critical'
          >
            {submitError}
          </Banner>
        </div>
      )}
      <div className='p-4 border-t border-gray-300'>{renderButtons()}</div>
    </form>
  )
}

export default RenewalsAddDependent
